import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { closestCenter, DndContext, DragEndEvent, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button, Input } from 'antd';
import * as cheerio from 'cheerio';
import React, { useState } from 'react';
import { toast } from 'react-toastify';

// 쿠팡 상품 관련 타입 정의
interface CoupangProduct {
  linkUrl: string;
  thumbnailImage: string;
  thunbnailAltText: string;
  width: number;
  height: number;
}

interface CoupangSection {
  id: string;
  title: string;
  description: string;
  coupangProducts: CoupangProduct[];
}

interface CoupangSectionsManagerProps {
  sections: CoupangSection[];
  onSectionsChange: (sections: CoupangSection[]) => void;
}

// 쿠팡 링크 파싱 함수
const parseCoupangLink = (htmlString: string) => {
  try {
    const $ = cheerio.load(htmlString);
    const linkElement = $('a[href*="link.coupang.com"]');
    const imgElement = linkElement.find('img');

    if (linkElement.length === 0 || imgElement.length === 0) {
      throw new Error('쿠팡 링크 형식이 올바르지 않습니다.');
    }

    const linkUrl = linkElement.attr('href') || '';
    const thumbnailImage = imgElement.attr('src') || '';
    const thunbnailAltText = imgElement.attr('alt') || '';
    const width = parseInt(imgElement.attr('width') || '0');
    const height = parseInt(imgElement.attr('height') || '0');

    return {
      linkUrl,
      thumbnailImage,
      thunbnailAltText,
      width,
      height,
    };
  } catch (error) {
    throw new Error('쿠팡 링크를 파싱할 수 없습니다.');
  }
};

// 드래그 가능한 쿠팡 상품 컴포넌트
interface SortableCoupangProductProps {
  id: string;
  product: CoupangProduct;
  onRemove: () => void;
}

const SortableCoupangProduct: React.FC<SortableCoupangProductProps> = ({ id, product, onRemove }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={{
        ...style,
        display: 'flex',
        alignItems: 'center',
        padding: '8px',
        border: '1px solid #eaeaea',
        borderRadius: '8px',
        backgroundColor: '#fafafa',
        cursor: 'grab',
      }}
      {...attributes}>
      <div
        style={{
          width: '20px',
          height: '20px',
          marginRight: '8px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#999',
          fontSize: '12px',
          cursor: 'grab',
        }}
        {...listeners}>
        ⋮⋮
      </div>
      <img
        src={product.thumbnailImage}
        alt={product.thunbnailAltText}
        style={{
          width: '60px',
          height: '60px',
          objectFit: 'cover',
          borderRadius: '4px',
          marginRight: '12px',
        }}
      />
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: '12px', fontWeight: 'bold', marginBottom: '4px' }}>{product.thunbnailAltText}</div>
        <div style={{ fontSize: '11px', color: '#666', wordBreak: 'break-all' }}>{product.linkUrl}</div>
        <div style={{ fontSize: '11px', color: '#999' }}>
          크기: {product.width} x {product.height}
        </div>
      </div>
      <Button
        type="text"
        danger
        icon={<DeleteOutlined />}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onRemove();
        }}
        onMouseDown={(e) => {
          e.stopPropagation();
        }}
        style={{ marginLeft: '8px' }}
      />
    </div>
  );
};

// 드래그 가능한 섹션 컴포넌트
interface SortableSectionProps {
  id: string;
  section: CoupangSection;
  onSectionChange: (section: CoupangSection) => void;
  onRemoveSection: () => void;
}

const SortableSection: React.FC<SortableSectionProps> = ({ id, section, onSectionChange, onRemoveSection }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={{
        ...style,
        border: '1px solid #eaeaea',
        borderRadius: '12px',
        padding: '16px',
        marginBottom: '16px',
        backgroundColor: '#fafafa',
        cursor: 'grab',
      }}
      {...attributes}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div
            style={{
              width: '20px',
              height: '20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#999',
              fontSize: '12px',
              cursor: 'grab',
            }}
            {...listeners}>
            ⋮⋮
          </div>
          <div style={{ fontSize: '16px', fontWeight: 'bold' }}>섹션 {section.id}</div>
        </div>
        <Button type="text" danger icon={<DeleteOutlined />} onClick={onRemoveSection}>
          섹션 삭제
        </Button>
      </div>

      <div style={{ marginBottom: '12px' }}>
        <div style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: '8px' }}>섹션 제목</div>
        <Input
          placeholder="섹션 제목을 입력하세요..."
          value={section.title}
          onChange={(e) => {
            onSectionChange({
              ...section,
              title: e.target.value,
            });
          }}
        />
      </div>

      <div style={{ marginBottom: '12px' }}>
        <div style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: '8px' }}>섹션 설명</div>
        <Input.TextArea
          placeholder="섹션 설명을 입력하세요..."
          value={section.description}
          onChange={(e) => {
            onSectionChange({
              ...section,
              description: e.target.value,
            });
          }}
          rows={3}
        />
      </div>

      <CoupangSectionManager section={section} onSectionChange={onSectionChange} onRemoveSection={onRemoveSection} />
    </div>
  );
};

// 섹션별 쿠팡 상품 관리 컴포넌트
interface CoupangSectionManagerProps {
  section: CoupangSection;
  onSectionChange: (section: CoupangSection) => void;
  onRemoveSection: () => void;
}

const CoupangSectionManager: React.FC<CoupangSectionManagerProps> = ({ section, onSectionChange }) => {
  const [linkInput, setLinkInput] = useState('');

  const handleAddProduct = () => {
    if (!linkInput.trim()) {
      toast.error('쿠팡 링크를 입력해주세요.');
      return;
    }

    try {
      const parsedData = parseCoupangLink(linkInput);
      const newProducts = [...section.coupangProducts, parsedData];
      onSectionChange({
        ...section,
        coupangProducts: newProducts,
      });
      setLinkInput('');
      toast.success('쿠팡 상품이 추가되었습니다.');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : '쿠팡 링크를 파싱할 수 없습니다.');
    }
  };

  const handleRemoveProduct = (index: number) => {
    const newProducts = section.coupangProducts.filter((_, i) => i !== index);
    onSectionChange({
      ...section,
      coupangProducts: newProducts,
    });
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = section.coupangProducts.findIndex((_, index) => index.toString() === active.id);
      const newIndex = section.coupangProducts.findIndex((_, index) => index.toString() === over.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        const newProducts = arrayMove(section.coupangProducts, oldIndex, newIndex);
        onSectionChange({
          ...section,
          coupangProducts: newProducts,
        });
      }
    }
  };

  return (
    <div>
      <div style={{ marginBottom: '12px' }}>
        <div style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: '8px' }}>
          {`쿠팡 상품 추가하기 (쿠팡파트너스 > 링크 생성 > 블로그용 태그 붙여넣기)`}
        </div>
        <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
          <Input.TextArea
            style={{ flex: 1 }}
            placeholder="쿠팡 링크를 붙여넣으세요..."
            value={linkInput}
            onChange={(e) => setLinkInput(e.target.value)}
            rows={2}
          />
          <Button type="primary" onClick={handleAddProduct}>
            추가
          </Button>
        </div>
      </div>

      {section.coupangProducts.length > 0 && (
        <div>
          <div style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: '8px' }}>
            상품 목록 (드래그하여 순서 변경)
          </div>
          <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext
              items={section.coupangProducts.map((_, index) => index.toString())}
              strategy={verticalListSortingStrategy}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {section.coupangProducts.map((product, index) => (
                  <SortableCoupangProduct
                    key={`coupang-product-${index}`}
                    id={index.toString()}
                    product={product}
                    onRemove={() => handleRemoveProduct(index)}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        </div>
      )}
    </div>
  );
};

// 메인 쿠팡 섹션 관리 컴포넌트
export const CoupangSectionsManager: React.FC<CoupangSectionsManagerProps> = ({ sections, onSectionsChange }) => {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 10 },
    }),
  );

  const addSection = () => {
    const newSection: CoupangSection = {
      id: `section-${Date.now()}`,
      title: '',
      description: '',
      coupangProducts: [],
    };
    onSectionsChange([...sections, newSection]);
  };

  const updateSection = (index: number, updatedSection: CoupangSection) => {
    const newSections = [...sections];
    newSections[index] = updatedSection;
    onSectionsChange(newSections);
  };

  const removeSection = (index: number) => {
    const newSections = sections.filter((_, i) => i !== index);
    onSectionsChange(newSections);
  };

  const handleSectionDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = sections.findIndex((section) => section.id === active.id);
      const newIndex = sections.findIndex((section) => section.id === over.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        const newSections = arrayMove(sections, oldIndex, newIndex);
        onSectionsChange(newSections);
      }
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <div style={{ fontSize: '16px', fontWeight: 'bold' }}>쿠팡 상품 섹션 ({sections.length}개)</div>
        <Button type="primary" icon={<PlusOutlined />} onClick={addSection}>
          섹션 추가
        </Button>
      </div>

      <div>
        {sections.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
            아직 추가된 섹션이 없습니다. "섹션 추가" 버튼을 클릭하여 섹션을 추가해보세요.
          </div>
        ) : (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleSectionDragEnd}>
            <SortableContext items={sections.map((section) => section.id)} strategy={verticalListSortingStrategy}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {sections.map((section, index) => (
                  <SortableSection
                    key={section.id}
                    id={section.id}
                    section={section}
                    onSectionChange={(updatedSection) => updateSection(index, updatedSection)}
                    onRemoveSection={() => removeSection(index)}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}
      </div>
    </div>
  );
};
